import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import MarkdownRenderer from '../components/MarkdownRenderer';
import MarkdownEditor from '../components/MarkdownEditor';

interface Ticket {
  id: number;
  ticket_id: string;
  company_id: number;
  company_name: string;
  company_email: string;
  user_id?: number;
  user_email?: string;
  user_name?: string;
  created_by?: number;
  created_by_email?: string;
  last_updated_by?: number;
  last_updated_by_email?: string;
  last_updated_by_company_id?: number;
  assigned_to?: number;
  assigned_to_email?: string;
  subject: string;
  description: string;
  status: string;
  priority: string;
  created_at: string;
  updated_at: string;
  cc_emails?: string;
  created_by_name?: string;
  is_outage?: number;
  outage_acknowledged_at?: string | null;
  outage_acknowledged_by?: number | null;
  acknowledged_by_email?: string | null;
  acknowledged_by_name?: string | null;
}

interface TicketReply {
  id: number;
  ticket_id: number;
  user_id: number;
  user_email: string;
  user_name?: string;
  user_role: string;
  user_company_id?: number;
  message: string;
  type: 'reply' | 'agent_reply' | 'internal';
  created_at: string;
  updated_at?: string;
}

interface TimeEntry {
  id: number;
  ticket_id: number;
  user_id: number;
  user_email: string;
  user_name?: string;
  start_time?: string;
  stop_time?: string;
  duration_minutes: number;
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface Reminder {
  id: number;
  ticket_id: number;
  created_by: number;
  created_by_email?: string;
  created_by_name?: string;
  scheduled_time: string;
  description: string;
  executed: number;
  created_at: string;
  updated_at: string;
}

interface EscalationLogEntry {
  id: number;
  ticket_id: number;
  event_type: string;
  user_id?: number | null;
  user_email?: string | null;
  user_name?: string | null;
  message: string;
  metadata?: string | null;
  created_at: string;
}

const ESCALATION_EVENT_LABELS: Record<string, string> = {
  marked_outage: 'SEV1 / Outage',
  sms_batch_started: 'SMS notifications',
  sms_sent: 'SMS sent',
  sms_failed: 'SMS failed',
  sms_skipped: 'SMS skipped',
  slack_notice: 'Slack',
  voice_call_placed: 'Voice call',
  voice_call_failed: 'Call failed',
  voice_call_acknowledged: 'Acknowledged (phone)',
  voice_call_declined: 'Declined (phone)',
  escalation_sequence: 'Call order',
  acknowledged_portal: 'Acknowledged (portal)',
  ack_updated: 'Ack updated',
};

function escalationEventBadgeClass(eventType: string): string {
  if (eventType.includes('acknowledged') || eventType === 'ack_updated') {
    return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
  }
  if (eventType.includes('failed') || eventType === 'voice_call_declined') {
    return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
  }
  if (eventType === 'sms_skipped') {
    return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
  }
  if (eventType === 'marked_outage') {
    return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
  }
  return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
}

const TicketDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [replies, setReplies] = useState<TicketReply[]>([]);
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [loading, setLoading] = useState(true);
  const [replyMessage, setReplyMessage] = useState('');
  const [replyType, setReplyType] = useState<'reply' | 'agent_reply' | 'internal'>('agent_reply');
  const [replyStatus, setReplyStatus] = useState<string>('');
  const [replyPriority, setReplyPriority] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [editingReplyId, setEditingReplyId] = useState<number | null>(null);
  const [editingMessage, setEditingMessage] = useState('');
  
  // Time tracking state
  const [showTimeEntryForm, setShowTimeEntryForm] = useState(false);
  const [timeEntryMode, setTimeEntryMode] = useState<'start_stop' | 'direct'>('start_stop');
  const [timeEntryStartTime, setTimeEntryStartTime] = useState('');
  const [timeEntryStopTime, setTimeEntryStopTime] = useState('');
  const [timeEntryDuration, setTimeEntryDuration] = useState('');
  const [timeEntryNotes, setTimeEntryNotes] = useState('');
  const [timeEntryUserId, setTimeEntryUserId] = useState<string>('');
  const [editingTimeEntryId, setEditingTimeEntryId] = useState<number | null>(null);
  const [editingTimeEntry, setEditingTimeEntry] = useState<Partial<TimeEntry> | null>(null);
  
  // Reminders state
  const [showReminderForm, setShowReminderForm] = useState(false);
  const [reminderScheduledTime, setReminderScheduledTime] = useState('');
  const [reminderDescription, setReminderDescription] = useState('');
  const [editingReminderId, setEditingReminderId] = useState<number | null>(null);
  const [editingReminder, setEditingReminder] = useState<Partial<Reminder> | null>(null);
  const [acking, setAcking] = useState(false);
  const [editingAck, setEditingAck] = useState(false);
  const [ackTimestamp, setAckTimestamp] = useState('');
  const [ackByUserId, setAckByUserId] = useState<string>('');
  const [agents, setAgents] = useState<{ id: number; name?: string; email: string }[]>([]);
  const [savingAck, setSavingAck] = useState(false);
  const [escalationLog, setEscalationLog] = useState<EscalationLogEntry[]>([]);
  const [escalationLogLoading, setEscalationLogLoading] = useState(false);

  const isGlobalAdmin = user?.role === 'admin' && !user?.company_id;
  const isGlobalUser = !user?.company_id;
  const isAgent = isGlobalAdmin || isGlobalUser;
  const isCompanyAdmin = user?.role === 'admin' && user?.company_id;

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Fallback copy failed:', err);
      }
      document.body.removeChild(textArea);
    }
  };

  const formatTimeToAcknowledge = (createdAt: string, acknowledgedAt: string): string => {
    const created = new Date(createdAt).getTime();
    const acknowledged = new Date(acknowledgedAt).getTime();
    const diffMs = acknowledged - created;
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    const remainingHours = diffHours % 24;
    const remainingMinutes = diffMinutes % 60;
    const remainingSeconds = diffSeconds % 60;

    const parts: string[] = [];

    if (diffDays > 0) {
      parts.push(`${diffDays} day${diffDays > 1 ? 's' : ''}`);
    }
    if (remainingHours > 0) {
      parts.push(`${remainingHours} hour${remainingHours > 1 ? 's' : ''}`);
    }
    if (remainingMinutes > 0 && diffHours < 24) {
      parts.push(`${remainingMinutes} minute${remainingMinutes > 1 ? 's' : ''}`);
    }
    if (parts.length === 0) {
      // Less than a minute
      parts.push(`${remainingSeconds} second${remainingSeconds !== 1 ? 's' : ''}`);
    }

    return parts.join(' ');
  };

  useEffect(() => {
    if (id) {
      fetchTicket();
      fetchReplies();
      fetchTimeEntries();
      if (isAgent) {
        fetchReminders();
      }
      if (isGlobalAdmin) {
        api.get('/users/agents').then((res) => setAgents(res.data || [])).catch(() => setAgents([]));
      }
    }
  }, [id]);

  useEffect(() => {
    if (ticket?.is_outage === 1 && id) {
      fetchEscalationLog();
    } else {
      setEscalationLog([]);
    }
  }, [ticket?.is_outage, id]);

  const fetchTicket = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/tickets/${id}`);
      setTicket(response.data);
      // Initialize reply status and priority with current ticket values
      if (response.data) {
        setReplyStatus(response.data.status || 'open');
        setReplyPriority(response.data.priority || 'medium');
      }
    } catch (error: any) {
      console.error('Error fetching ticket:', error);
      if (error.response?.status === 404) {
        setError('Ticket not found');
      } else if (error.response?.status === 403) {
        setError('Access denied');
      } else {
        setError('Error loading ticket');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchReplies = async () => {
    if (!id) return;
    try {
      const response = await api.get(`/tickets/${id}/replies`);
      setReplies(response.data);
    } catch (error) {
      console.error('Error fetching replies:', error);
    }
  };

  const fetchEscalationLog = async () => {
    if (!id) return;
    try {
      setEscalationLogLoading(true);
      const response = await api.get(`/tickets/${id}/escalation-log`);
      setEscalationLog(response.data || []);
    } catch (error) {
      console.error('Error fetching escalation log:', error);
      setEscalationLog([]);
    } finally {
      setEscalationLogLoading(false);
    }
  };

  const fetchTimeEntries = async () => {
    if (!id) return;
    try {
      const response = await api.get(`/tickets/${id}/time-entries`);
      setTimeEntries(response.data);
    } catch (error) {
      console.error('Error fetching time entries:', error);
    }
  };

  const fetchReminders = async () => {
    if (!id) return;
    try {
      const response = await api.get(`/tickets/${id}/reminders`);
      setReminders(response.data);
    } catch (error) {
      console.error('Error fetching reminders:', error);
    }
  };

  const handleAckOutage = async () => {
    if (!ticket || !id) return;
    try {
      setAcking(true);
      await api.post(`/tickets/${id}/ack`);
      await fetchTicket();
      await fetchEscalationLog();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to acknowledge ticket');
    } finally {
      setAcking(false);
    }
  };

  const openEditAck = () => {
    if (!ticket?.outage_acknowledged_at) return;
    const d = new Date(ticket.outage_acknowledged_at);
    const pad = (n: number) => String(n).padStart(2, '0');
    setAckTimestamp(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
    setAckByUserId(ticket.outage_acknowledged_by != null ? String(ticket.outage_acknowledged_by) : '');
    setEditingAck(true);
    api.get('/users/agents').then((res) => setAgents(res.data || [])).catch(() => setAgents([]));
  };

  const handleSaveAck = async () => {
    if (!id || !ackTimestamp.trim()) return;
    try {
      setSavingAck(true);
      const payload: { outage_acknowledged_at: string; outage_acknowledged_by?: number | null } = {
        outage_acknowledged_at: new Date(ackTimestamp).toISOString(),
      };
      if (ackByUserId !== undefined) {
        payload.outage_acknowledged_by = ackByUserId === '' ? null : parseInt(ackByUserId, 10);
      }
      await api.patch(`/tickets/${id}/ack`, payload);
      setEditingAck(false);
      await fetchTicket();
      await fetchEscalationLog();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to update acknowledge timestamp');
    } finally {
      setSavingAck(false);
    }
  };

  const handleAddReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ticket || !replyMessage.trim()) return;

    try {
      const payload: any = {
        message: replyMessage,
      };
      
      if (isAgent) {
        payload.type = replyType;
        // Include status and priority if they differ from current ticket values
        if (replyStatus && replyStatus !== ticket.status) {
          payload.status = replyStatus;
        }
        if (replyPriority && replyPriority !== ticket.priority) {
          payload.priority = replyPriority;
        }
      } else {
        payload.type = 'reply';
      }

      await api.post(`/tickets/${ticket.id}/replies`, payload);
      setReplyMessage('');
      if (isAgent) {
        setReplyType('agent_reply');
      }
      fetchReplies();
      fetchTicket(); // Refresh ticket to update updated_at, status, and priority
    } catch (error: any) {
      alert(error.response?.data?.error || 'Error adding reply');
    }
  };

  const handleEditReply = async (replyId: number) => {
    try {
      await api.put(`/tickets/replies/${replyId}`, { message: editingMessage });
      setEditingReplyId(null);
      setEditingMessage('');
      fetchReplies();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Error updating reply');
    }
  };

  const handleDeleteReply = async (replyId: number) => {
    if (!window.confirm('Are you sure you want to delete this reply?')) return;
    try {
      await api.delete(`/tickets/replies/${replyId}`);
      fetchReplies();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Error deleting reply');
    }
  };

  const handleCloseTicket = async () => {
    if (!ticket) return;
    if (!window.confirm('Are you sure you want to close this ticket?')) return;
    
    try {
      await api.put(`/tickets/${ticket.id}`, { status: 'closed' });
      fetchTicket(); // Refresh ticket to show updated status
      setReplyMessage(''); // Clear reply field
    } catch (error: any) {
      alert(error.response?.data?.error || 'Error closing ticket');
    }
  };

  const handleAddTimeEntry = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ticket) return;

    try {
      const payload: any = { notes: timeEntryNotes || null };

      if (timeEntryMode === 'start_stop') {
        if (!timeEntryStartTime || !timeEntryStopTime) {
          alert('Please provide both start time and stop time');
          return;
        }
        payload.start_time = timeEntryStartTime;
        payload.stop_time = timeEntryStopTime;
      } else {
        if (!timeEntryDuration || parseFloat(timeEntryDuration) <= 0) {
          alert('Please provide a valid duration in minutes');
          return;
        }
        payload.duration_minutes = Math.round(parseFloat(timeEntryDuration) * 60); // Convert hours to minutes
      }

      if (isGlobalAdmin && timeEntryUserId) {
        payload.user_id = parseInt(timeEntryUserId, 10);
      }

      await api.post(`/tickets/${ticket.id}/time-entries`, payload);
      
      // Reset form
      setShowTimeEntryForm(false);
      setTimeEntryMode('start_stop');
      setTimeEntryStartTime('');
      setTimeEntryStopTime('');
      setTimeEntryDuration('');
      setTimeEntryNotes('');
      setTimeEntryUserId('');
      
      fetchTimeEntries();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Error adding time entry');
    }
  };

  const handleEditTimeEntry = async (entryId: number) => {
    try {
      const payload: any = { notes: editingTimeEntry?.notes || null };

      if (editingTimeEntry?.start_time && editingTimeEntry?.stop_time) {
        payload.start_time = editingTimeEntry.start_time;
        payload.stop_time = editingTimeEntry.stop_time;
      } else if (editingTimeEntry?.duration_minutes !== undefined) {
        payload.duration_minutes = editingTimeEntry.duration_minutes;
      }

      await api.put(`/tickets/time-entries/${entryId}`, payload);
      
      setEditingTimeEntryId(null);
      setEditingTimeEntry(null);
      fetchTimeEntries();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Error updating time entry');
    }
  };

  const handleDeleteTimeEntry = async (entryId: number) => {
    if (!window.confirm('Are you sure you want to delete this time entry?')) return;
    
    try {
      await api.delete(`/tickets/time-entries/${entryId}`);
      fetchTimeEntries();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Error deleting time entry');
    }
  };

  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0 && mins > 0) {
      return `${hours}h ${mins}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${mins}m`;
    }
  };

  const getTotalTime = (): number => {
    return timeEntries.reduce((total, entry) => total + entry.duration_minutes, 0);
  };

  const handleAddReminder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ticket || !reminderScheduledTime || !reminderDescription.trim()) return;

    try {
      // Convert local datetime to ISO string
      const scheduledTime = new Date(reminderScheduledTime).toISOString();
      
      await api.post(`/tickets/${ticket.id}/reminders`, {
        scheduled_time: scheduledTime,
        description: reminderDescription
      });
      
      // Reset form
      setShowReminderForm(false);
      setReminderScheduledTime('');
      setReminderDescription('');
      
      fetchReminders();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Error adding reminder');
    }
  };

  const handleEditReminder = async (reminderId: number) => {
    try {
      const payload: any = {};
      if (editingReminder?.scheduled_time) {
        payload.scheduled_time = new Date(editingReminder.scheduled_time).toISOString();
      }
      if (editingReminder?.description !== undefined) {
        payload.description = editingReminder.description;
      }

      await api.put(`/tickets/reminders/${reminderId}`, payload);
      
      setEditingReminderId(null);
      setEditingReminder(null);
      fetchReminders();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Error updating reminder');
    }
  };

  const handleDeleteReminder = async (reminderId: number) => {
    if (!window.confirm('Are you sure you want to delete this reminder?')) return;
    
    try {
      await api.delete(`/tickets/reminders/${reminderId}`);
      fetchReminders();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Error deleting reminder');
    }
  };


  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-yellow-100 text-yellow-800';
      case 'pending':
        return 'bg-blue-100 text-blue-800';
      case 'resolved':
        return 'bg-green-100 text-green-800';
      case 'closed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto"></div>
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error || 'Ticket not found'}</p>
          <Link
            to="/tickets"
            className="mt-4 inline-block text-sm text-brand hover:opacity-90"
          >
            ← Back to Tickets
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 sm:px-0">
      <div className="mb-6">
        <Link
          to="/tickets"
          className="text-sm text-brand hover:opacity-90 mb-4 inline-block"
        >
          ← Back to Tickets
        </Link>
        <div className="flex justify-between items-start mt-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              {ticket.subject}
            </h1>
            <div className="mt-2 flex items-center space-x-2">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Ticket ID: <span className="font-mono font-semibold">{ticket.ticket_id || `TKT-${String(ticket.id).padStart(6, '0')}`}</span>
              </p>
              <button
                onClick={() => copyToClipboard(ticket.ticket_id || `TKT-${String(ticket.id).padStart(6, '0')}`)}
                className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 hover:text-brand hover:bg-gray-100 rounded focus:outline-none focus:ring-2 focus:ring-brand"
                title="Copy Ticket ID to clipboard"
              >
                {copied ? (
                  <>
                    <svg className="w-4 h-4 mr-1 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Copied!
                  </>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
          <div className="flex items-center space-x-2 flex-wrap gap-2">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(ticket.status)}`}>
              {ticket.status.replace('_', ' ')}
            </span>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(ticket.priority)}`}>
              {ticket.priority}
            </span>
            {ticket.is_outage === 1 && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                Outage / SEV1
              </span>
            )}
            {ticket.is_outage === 1 && ticket.outage_acknowledged_at && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                Acknowledged
              </span>
            )}
            {ticket.is_outage === 1 && !ticket.outage_acknowledged_at && isAgent && (
              <button
                type="button"
                onClick={handleAckOutage}
                disabled={acking}
                className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {acking ? 'Acknowledging…' : 'Acknowledge'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Acknowledgement Info for Outage Tickets */}
      {ticket.is_outage === 1 && ticket.outage_acknowledged_at && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start flex-1 min-w-0">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3 flex-1 min-w-0">
                <h3 className="text-sm font-medium text-green-800 dark:text-green-300">
                  Outage Acknowledged
                </h3>
                {!editingAck ? (
                  <div className="mt-2 text-sm text-green-700 dark:text-green-400">
                    <p>
                      <span className="font-medium">Acknowledged by:</span>{' '}
                      {ticket.acknowledged_by_name || ticket.acknowledged_by_email || 'Unknown'}
                    </p>
                    {ticket.outage_acknowledged_at && ticket.created_at && (
                      <p className="mt-1">
                        <span className="font-medium">Time to Acknowledge:</span>{' '}
                        {formatTimeToAcknowledge(ticket.created_at, ticket.outage_acknowledged_at)}
                      </p>
                    )}
                    {ticket.outage_acknowledged_at && (
                      <p className="mt-1 text-xs text-green-600 dark:text-green-500">
                        Acknowledged at: {new Date(ticket.outage_acknowledged_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="mt-3 space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-green-800 dark:text-green-300 mb-1">Acknowledged at</label>
                      <input
                        type="datetime-local"
                        value={ackTimestamp}
                        onChange={(e) => setAckTimestamp(e.target.value)}
                        className="block w-full max-w-xs rounded-md border border-green-300 dark:border-green-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm py-1.5 px-2"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-green-800 dark:text-green-300 mb-1">Acknowledged by</label>
                      <select
                        value={ackByUserId}
                        onChange={(e) => setAckByUserId(e.target.value)}
                        className="block w-full max-w-xs rounded-md border border-green-300 dark:border-green-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm py-1.5 px-2"
                      >
                        <option value="">— No one —</option>
                        {agents.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.name || a.email}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={handleSaveAck}
                        disabled={savingAck || !ackTimestamp.trim()}
                        className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium text-white bg-green-700 hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {savingAck ? 'Saving…' : 'Save'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingAck(false)}
                        className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium text-green-800 dark:text-green-300 bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-800/40 focus:outline-none"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            {isGlobalAdmin && !editingAck && (
              <button
                type="button"
                onClick={openEditAck}
                className="flex-shrink-0 inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium text-green-800 dark:text-green-300 bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-800/40 focus:outline-none"
              >
                Update timestamp
              </button>
            )}
          </div>
        </div>
      )}

      {/* Escalation log for outage tickets */}
      {ticket.is_outage === 1 && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Escalation log
            </h3>
            <button
              type="button"
              onClick={fetchEscalationLog}
              disabled={escalationLogLoading}
              className="text-xs text-brand hover:opacity-90 disabled:opacity-50"
            >
              {escalationLogLoading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
          {escalationLogLoading && escalationLog.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading escalation history…</p>
          ) : escalationLog.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No escalation events recorded yet.</p>
          ) : (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {escalationLog.map((entry) => (
                <li key={entry.id} className="py-3 first:pt-0 last:pb-0">
                  <div className="flex flex-wrap items-start gap-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${escalationEventBadgeClass(entry.event_type)}`}>
                      {ESCALATION_EVENT_LABELS[entry.event_type] || entry.event_type}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {new Date(entry.created_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-gray-800 dark:text-gray-200">{entry.message}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {(isGlobalAdmin || isCompanyAdmin) && (
              <Link
                to="/tickets"
                className="inline-flex items-center px-3 py-1 rounded-md text-sm font-medium text-brand hover:opacity-90 hover:bg-brand-muted dark:hover:bg-brand-muted/20 focus:outline-none focus:ring-2 focus:ring-brand"
                onClick={(e) => {
                  // Store ticket ID in sessionStorage so Tickets page can auto-open edit modal
                  sessionStorage.setItem('editTicketId', ticket.id.toString());
                }}
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit
              </Link>
            )}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {(isGlobalAdmin || isGlobalUser) && (
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Company</label>
              <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{ticket.company_name}</p>
            </div>
          )}
          {ticket.user_email && (
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">User</label>
              <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                {ticket.user_name ? `${ticket.user_name} (${ticket.user_email})` : ticket.user_email}
              </p>
            </div>
          )}
          {ticket.created_by_email && (
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Created By</label>
              <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                {ticket.created_by_name 
                  ? `${ticket.created_by_name} (${ticket.created_by_email})`
                  : ticket.created_by_email}
              </p>
            </div>
          )}
          {ticket.last_updated_by_email && (
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Last Updated By</label>
              <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                {ticket.last_updated_by_company_id === null || ticket.last_updated_by_company_id === undefined
                  ? 'Agent'
                  : 'Customer'} ({ticket.last_updated_by_email})
              </p>
            </div>
          )}
          {ticket.assigned_to_email && (
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Assigned To</label>
              <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{ticket.assigned_to_email}</p>
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Created</label>
            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{new Date(ticket.created_at).toLocaleString()}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Last Updated</label>
            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{new Date(ticket.updated_at).toLocaleString()}</p>
          </div>
          {ticket.cc_emails && (
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">CC</label>
              <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{ticket.cc_emails}</p>
            </div>
          )}
        </div>

        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Description</label>
          <div className="mt-2 text-sm text-gray-700 dark:text-gray-300">
            <MarkdownRenderer content={ticket.description} />
          </div>
        </div>
      </div>

      {/* Time Tracking Section */}
      {isAgent && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Time Tracking</h2>
            {!showTimeEntryForm && (
              <button
                onClick={() => setShowTimeEntryForm(true)}
                className="px-4 py-2 text-sm font-medium text-white bg-brand hover:bg-brand-hover rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand"
              >
                Add Time Entry
              </button>
            )}
          </div>

          {/* Total Time Summary */}
          {timeEntries.length > 0 && (
            <div className="mb-4 p-3 bg-brand-muted rounded-lg">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Total Time: <span className="text-brand font-semibold">{formatDuration(getTotalTime())}</span>
              </p>
            </div>
          )}

          {/* Time Entry Form */}
          {showTimeEntryForm && (
            <form onSubmit={handleAddTimeEntry} className="mb-6 p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
              {isGlobalAdmin && agents.length > 0 && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    User
                  </label>
                  <select
                    value={timeEntryUserId}
                    onChange={(e) => setTimeEntryUserId(e.target.value)}
                    className="block w-full max-w-xs border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-brand focus:border-brand"
                  >
                    <option value="">Me ({user?.email})</option>
                    {agents.filter((a) => a.id !== user?.id).map((a) => (
                      <option key={a.id} value={String(a.id)}>
                        {a.name ? `${a.name} (${a.email})` : a.email}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Entry Method
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="start_stop"
                      checked={timeEntryMode === 'start_stop'}
                      onChange={(e) => {
                        setTimeEntryMode(e.target.value as 'start_stop' | 'direct');
                        setTimeEntryDuration(''); // Clear direct duration when switching to start/stop
                      }}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Start/Stop Time</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="direct"
                      checked={timeEntryMode === 'direct'}
                      onChange={(e) => {
                        setTimeEntryMode(e.target.value as 'start_stop' | 'direct');
                        setTimeEntryStartTime(''); // Clear start time when switching to direct
                        setTimeEntryStopTime(''); // Clear stop time when switching to direct
                      }}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Direct Time Entry</span>
                  </label>
                </div>
              </div>

              {timeEntryMode === 'start_stop' ? (
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Start Time
                    </label>
                    <input
                      type="datetime-local"
                      value={timeEntryStartTime}
                      onChange={(e) => setTimeEntryStartTime(e.target.value)}
                      className="block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-brand focus:border-brand"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Stop Time
                    </label>
                    <input
                      type="datetime-local"
                      value={timeEntryStopTime}
                      onChange={(e) => setTimeEntryStopTime(e.target.value)}
                      className="block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-brand focus:border-brand"
                      required
                    />
                  </div>
                </div>
              ) : (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Duration (hours)
                  </label>
                  <input
                    type="number"
                    step="0.25"
                    min="0"
                    value={timeEntryDuration}
                    onChange={(e) => setTimeEntryDuration(e.target.value)}
                    className="block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-brand focus:border-brand"
                    placeholder="e.g., 1.5 for 1 hour 30 minutes"
                    required
                  />
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Notes (optional)
                </label>
                <textarea
                  value={timeEntryNotes}
                  onChange={(e) => setTimeEntryNotes(e.target.value)}
                  rows={3}
                  className="block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-brand focus:border-brand"
                  placeholder="Describe the work performed..."
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowTimeEntryForm(false);
                    setTimeEntryStartTime('');
                    setTimeEntryStopTime('');
                    setTimeEntryDuration('');
                    setTimeEntryNotes('');
                    setTimeEntryUserId('');
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 hover:bg-gray-50 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-brand hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand"
                >
                  Add Entry
                </button>
              </div>
            </form>
          )}

          {/* Time Entries List */}
          <div className="space-y-3">
            {timeEntries.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No time entries yet</p>
            ) : (
              timeEntries.map((entry) => (
                <div
                  key={entry.id}
                  className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg"
                >
                  {editingTimeEntryId === entry.id ? (
                    <div>
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        {editingTimeEntry?.start_time && editingTimeEntry?.stop_time ? (
                          <>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Start Time
                              </label>
                              <input
                                type="datetime-local"
                                value={editingTimeEntry.start_time ? new Date(editingTimeEntry.start_time).toISOString().slice(0, 16) : ''}
                                onChange={(e) => {
                                  const dateValue = e.target.value;
                                  setEditingTimeEntry({ 
                                    ...editingTimeEntry, 
                                    start_time: dateValue ? new Date(dateValue).toISOString() : undefined,
                                    duration_minutes: undefined // Clear duration when using start/stop
                                  });
                                }}
                                className="block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-1 px-2 text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Stop Time
                              </label>
                              <input
                                type="datetime-local"
                                value={editingTimeEntry.stop_time ? new Date(editingTimeEntry.stop_time).toISOString().slice(0, 16) : ''}
                                onChange={(e) => {
                                  const dateValue = e.target.value;
                                  setEditingTimeEntry({ 
                                    ...editingTimeEntry, 
                                    stop_time: dateValue ? new Date(dateValue).toISOString() : undefined,
                                    duration_minutes: undefined // Clear duration when using start/stop
                                  });
                                }}
                                className="block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-1 px-2 text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                              />
                            </div>
                          </>
                        ) : (
                          <div className="col-span-2">
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Duration (hours)
                            </label>
                            <input
                              type="number"
                              step="0.25"
                              min="0"
                              value={editingTimeEntry?.duration_minutes ? (editingTimeEntry.duration_minutes / 60).toFixed(2) : ''}
                              onChange={(e) => setEditingTimeEntry({ ...editingTimeEntry, duration_minutes: Math.round(parseFloat(e.target.value || '0') * 60), start_time: undefined, stop_time: undefined })}
                              className="block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-1 px-2 text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            />
                          </div>
                        )}
                      </div>
                      <div className="mb-4">
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Notes
                        </label>
                        <textarea
                          value={editingTimeEntry?.notes || ''}
                          onChange={(e) => setEditingTimeEntry({ ...editingTimeEntry, notes: e.target.value })}
                          rows={2}
                          className="block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-1 px-2 text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        />
                      </div>
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => {
                            setEditingTimeEntryId(null);
                            setEditingTimeEntry(null);
                          }}
                          className="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => handleEditTimeEntry(entry.id)}
                          className="px-3 py-1 text-xs font-medium text-white bg-brand rounded-md hover:bg-brand-hover"
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {entry.user_name && entry.user_email
                              ? `${entry.user_name} (${entry.user_email})`
                              : entry.user_name || entry.user_email}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {entry.start_time && entry.stop_time ? (
                              <>
                                {new Date(entry.start_time).toLocaleString()} - {new Date(entry.stop_time).toLocaleString()}
                              </>
                            ) : (
                              `Duration: ${formatDuration(entry.duration_minutes)}`
                            )}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-semibold text-brand">
                            {formatDuration(entry.duration_minutes)}
                          </span>
                          <button
                            onClick={() => {
                              setEditingTimeEntryId(entry.id);
                              setEditingTimeEntry({
                                start_time: entry.start_time,
                                stop_time: entry.stop_time,
                                duration_minutes: entry.duration_minutes,
                                notes: entry.notes
                              });
                            }}
                            className="text-gray-400 hover:text-brand transition-colors"
                            title="Edit entry"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDeleteTimeEntry(entry.id)}
                            className="text-gray-400 hover:text-red-600 transition-colors"
                            title="Delete entry"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-4v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      {entry.notes && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">{entry.notes}</p>
                      )}
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                        Added {new Date(entry.created_at).toLocaleString()}
                      </p>
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Reminders Section */}
      {isAgent && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Reminders</h2>
            {!showReminderForm && (
              <button
                onClick={() => setShowReminderForm(true)}
                className="px-4 py-2 text-sm font-medium text-white bg-brand hover:bg-brand-hover rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand"
              >
                Add Reminder
              </button>
            )}
          </div>

          {/* Reminder Form */}
          {showReminderForm && (
            <form onSubmit={handleAddReminder} className="mb-6 p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Scheduled Time
                </label>
                <input
                  type="datetime-local"
                  value={reminderScheduledTime}
                  onChange={(e) => setReminderScheduledTime(e.target.value)}
                  className="block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-brand focus:border-brand"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={reminderDescription}
                  onChange={(e) => setReminderDescription(e.target.value)}
                  rows={3}
                  className="block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-brand focus:border-brand"
                  placeholder="What should this reminder say when it's posted as a reply?"
                  required
                />
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowReminderForm(false);
                    setReminderScheduledTime('');
                    setReminderDescription('');
                  }}
                  className="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-3 py-1 text-xs font-medium text-white bg-brand rounded-md hover:bg-brand-hover"
                >
                  Add Reminder
                </button>
              </div>
            </form>
          )}

          {/* Reminders List */}
          <div className="space-y-3">
            {reminders.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No reminders set</p>
            ) : (
              reminders.map((reminder) => (
                <div
                  key={reminder.id}
                  className={`p-4 rounded-lg border ${
                    reminder.executed
                      ? 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 opacity-60'
                      : new Date(reminder.scheduled_time) <= new Date()
                      ? 'bg-yellow-50 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-800'
                      : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600'
                  }`}
                >
                  {editingReminderId === reminder.id ? (
                    <div>
                      <div className="mb-4">
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Scheduled Time
                        </label>
                        <input
                          type="datetime-local"
                          value={editingReminder?.scheduled_time ? new Date(editingReminder.scheduled_time).toISOString().slice(0, 16) : ''}
                          onChange={(e) => setEditingReminder({ ...editingReminder, scheduled_time: e.target.value ? new Date(e.target.value).toISOString() : undefined })}
                          className="block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-1 px-2 text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        />
                      </div>
                      <div className="mb-4">
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Description
                        </label>
                        <textarea
                          value={editingReminder?.description || ''}
                          onChange={(e) => setEditingReminder({ ...editingReminder, description: e.target.value })}
                          rows={2}
                          className="block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-1 px-2 text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        />
                      </div>
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => {
                            setEditingReminderId(null);
                            setEditingReminder(null);
                          }}
                          className="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => handleEditReminder(reminder.id)}
                          className="px-3 py-1 text-xs font-medium text-white bg-brand rounded-md hover:bg-brand-hover"
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {new Date(reminder.scheduled_time).toLocaleString()}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Created by {reminder.created_by_name || reminder.created_by_email}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          {reminder.executed ? (
                            <span className="text-xs font-medium text-green-600 dark:text-green-400">Executed</span>
                          ) : new Date(reminder.scheduled_time) <= new Date() ? (
                            <span className="text-xs font-medium text-yellow-600 dark:text-yellow-400">Due</span>
                          ) : (
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Scheduled</span>
                          )}
                          {!reminder.executed && (
                            <>
                              <button
                                onClick={() => {
                                  setEditingReminderId(reminder.id);
                                  setEditingReminder({
                                    scheduled_time: reminder.scheduled_time,
                                    description: reminder.description
                                  });
                                }}
                                className="text-gray-400 hover:text-brand transition-colors"
                                title="Edit reminder"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleDeleteReminder(reminder.id)}
                                className="text-gray-400 hover:text-red-600 transition-colors"
                                title="Delete reminder"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-4v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-gray-700 dark:text-gray-300 mt-2">{reminder.description}</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                        Created {new Date(reminder.created_at).toLocaleString()}
                      </p>
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Replies Section */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Replies</h2>
        <div className="space-y-4 mb-6">
          {replies.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">No replies yet</p>
          ) : (
            replies.map((reply) => {
              const isInternal = reply.type === 'internal';
              const isAgentReply = reply.type === 'agent_reply';
              const isCustomerReply = reply.type === 'reply';

              return (
                <div
                  key={reply.id}
                  className={`p-4 rounded-lg border ${
                    isInternal
                      ? 'bg-yellow-50 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-800'
                      : isAgentReply
                      ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800'
                      : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600'
                  }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center space-x-2">
                      <span className={`text-sm font-medium ${
                        isInternal
                          ? 'text-gray-900 dark:text-yellow-100'
                          : isAgentReply
                          ? 'text-gray-900 dark:text-blue-100'
                          : 'text-gray-900 dark:text-gray-100'
                      }`}>
                        {reply.user_name ? `${reply.user_name} (${reply.user_email})` : reply.user_email}
                      </span>
                      {isInternal && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-100">
                          Internal Note
                        </span>
                      )}
                      {isAgentReply && !isInternal && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-100">
                          Agent
                        </span>
                      )}
                      {isCustomerReply && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                          Customer
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-4">
                      {isAgent && (
                        <div className="flex items-center space-x-2 mr-2">
                          <button
                            onClick={() => {
                              setEditingReplyId(reply.id);
                              setEditingMessage(reply.message);
                            }}
                            className="text-gray-400 hover:text-brand transition-colors"
                            title="Edit reply"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDeleteReply(reply.id)}
                            className="text-gray-400 hover:text-red-600 transition-colors"
                            title="Delete reply"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-4v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      )}
                      <span className={`text-xs ${
                        isInternal
                          ? 'text-gray-600 dark:text-yellow-200'
                          : isAgentReply
                          ? 'text-gray-600 dark:text-blue-200'
                          : 'text-gray-500 dark:text-gray-400'
                      }`}>
                        {new Date(reply.created_at).toLocaleString()}
                        {reply.updated_at && reply.updated_at !== reply.created_at && (
                          <span className="ml-1 italic">(edited)</span>
                        )}
                      </span>
                    </div>
                  </div>
                  <div className={`text-sm ${
                    isInternal
                      ? 'text-gray-800 dark:text-yellow-50'
                      : isAgentReply
                      ? 'text-gray-800 dark:text-blue-50'
                      : 'text-gray-700 dark:text-gray-300'
                  }`}>
                    {editingReplyId === reply.id ? (
                      <div className="mt-2">
                        <MarkdownEditor
                          value={editingMessage}
                          onChange={setEditingMessage}
                          placeholder="Edit your reply..."
                        />
                        <div className="mt-2 flex justify-end space-x-2">
                          <button
                            onClick={() => {
                              setEditingReplyId(null);
                              setEditingMessage('');
                            }}
                            className="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleEditReply(reply.id)}
                            className="px-3 py-1 text-xs font-medium text-white bg-brand rounded-md hover:bg-brand-hover focus:outline-none"
                          >
                            Save Changes
                          </button>
                        </div>
                      </div>
                    ) : (
                      <MarkdownRenderer content={reply.message} />
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Add Reply Form */}
        <form onSubmit={handleAddReply} className="border-t dark:border-gray-700 pt-4">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {isAgent ? 'Add Reply or Internal Note' : 'Add Reply'}
            </label>
            {isAgent && (
              <>
                <div className="mb-2">
                  <select
                    value={replyType}
                    onChange={(e) => setReplyType(e.target.value as 'reply' | 'agent_reply' | 'internal')}
                    className="block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-brand focus:border-brand"
                  >
                    <option value="agent_reply">Agent Reply (Visible to Customer)</option>
                    <option value="internal">Internal Note (Agents Only)</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                    <select
                      value={replyStatus}
                      onChange={(e) => setReplyStatus(e.target.value)}
                      className="block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-brand focus:border-brand"
                    >
                      <option value="open">Open</option>
                      <option value="pending">Pending</option>
                      <option value="resolved">Resolved</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Priority</label>
                    <select
                      value={replyPriority}
                      onChange={(e) => setReplyPriority(e.target.value)}
                      className="block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-brand focus:border-brand"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                </div>
              </>
            )}
            <MarkdownEditor
              value={replyMessage}
              onChange={setReplyMessage}
              placeholder="Type your reply..."
            />
          </div>
          <div className="flex justify-end space-x-3">
            {ticket && ticket.status !== 'closed' && (
              <button
                type="button"
                onClick={handleCloseTicket}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 hover:bg-gray-50 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand"
              >
                Close Ticket
              </button>
            )}
            <button
              type="submit"
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-brand hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand"
            >
              {replyType === 'internal' ? 'Add Internal Note' : 'Add Reply'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TicketDetail;
